import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { DestroyBase } from '../../../../components/common/ng-destroy-base';
import { ReportDesignerHostComponent } from '../../report-designer-host/report-designer-host.component';
import { EventBusService } from '../../../../services/event-bus.service';
import { ReportDesignerDocumentComponent } from '../report-designer-document/report-designer-document.component';
import { WidgetBaseComponent } from '../../widgets/widgetBase';
import { ReportDesignerWidgetCalculationOption } from '../../models/report-designer-widget-calculation-option';

/**
 * Report designer infobar
 */
@Component({
  selector: 'report-designer-infobar',
  templateUrl: './report-designer-infobar.component.html',
  styleUrls: ['./report-designer-infobar.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ReportDesignerInfobarComponent extends DestroyBase implements OnInit {
  /**
   * RD host component
   */
  @Input() public main: ReportDesignerHostComponent;

  /**
   * Constructor
   * @param eventBus EventBusService
   */
  constructor(private eventBus: EventBusService) {
    super();
  }

  /**
   * Selected tab
   */
  public selectedTabIndex = 0;

  /**
   * Current layout
   */
  public currentLayout: string = null;

  /**
   * Current calculation
   */
  public currentCalculation: number = null;

  /**
   * The active widget
   */
  public activeWidget: WidgetBaseComponent = null;

  /**
   * Layouts data source
   */
  public layoutsDataSource: string[] = [];

  /**
   * Calculation data source
   */
  public calculationDataSource: ReportDesignerWidgetCalculationOption[] = [];

  /**
   * Show empty hashtags?
   */
  public showEmptyHashtags = false;

  /**
   * Layout change handler
   */
  public layoutChanged(): void {
    if (this.currentLayout) {
      this.activeWidget.currentLayout = this.currentLayout;
      this.activeWidget.layoutChanged(this.currentLayout);
    }
  }

  /**
   * Calculation changed
   */
  public calculationChanged(): void {
    if (this.currentCalculation) {
      this.activeWidget.currentCalculation = this.currentCalculation;
      this.activeWidget.calculationChanged(this.currentCalculation);
    }
  }


  /**
   * Gets PIX Comps data
   */
  public getPixCompsData(): void{
    this.activeWidget.getPixCompsData();
  }

  /**
   * Activates the infobar
   * @param item
   * @returns
   */
  public activateInfobar(item: WidgetBaseComponent): void{
    const isSame = (this.activeWidget === item);
    if (item == null) {

      this.layoutsDataSource = [];
      this.calculationDataSource = [];

      this.activeWidget = null;
      return;
    }
    if (!isSame) {
      this.activeWidget = null;
    }

    this.selectedTabIndex = 0;
    this.activeWidget = item;

    this.processLayouts(item);

    this.processCalculations(item);

    this.currentLayout = this.activeWidget.currentLayout;
    this.currentCalculation = this.activeWidget.currentCalculation;
  }

  /**
   * Process the calculation options
   * @param item
   */
  private processCalculations(item: WidgetBaseComponent): void {
    let availableCalculations: ReportDesignerWidgetCalculationOption[];

    if (item.widgetConfig.oneInstancePerCalculation) {
      this.calculationDataSource = [];
      const activeDocument = this.main.getActiveDocument();
      availableCalculations = item.widgetConfig.widgetSettings.availableCalculations;
      const usedCalculations = this.getUsedCalculations(activeDocument, item);
      const choosableCalculations = this.getChoosableCalculations(item, availableCalculations, usedCalculations);

      const currentCalculationObject = availableCalculations.filter((elem) => elem.id === item.currentCalculation)[0];

      if (choosableCalculations.indexOf(currentCalculationObject) < 0) {
        choosableCalculations.unshift(currentCalculationObject);
      }
      this.calculationDataSource = choosableCalculations;
    }
    else if (item?.availableCalculations) {
      this.calculationDataSource = item.availableCalculations;
    }
    else {
      this.calculationDataSource = [];
    }
  }

  /**
   * Process the layout options
   * @param item
   */
  private processLayouts(item: WidgetBaseComponent): void {
    let availableLayouts = item.availableLayouts;

    if (item.widgetConfig.oneInstancePerLayout) {
      this.layoutsDataSource = [];
      const activeDocument = this.main.getActiveDocument();
      availableLayouts = item.widgetConfig.widgetSettings.availableLayouts;
      const usedLayouts = this.getUsedLayouts(activeDocument, item);
      const choosableLayouts = this.getChoosableLayouts(availableLayouts, usedLayouts);

      if (item.widgetConfig.widgetId != 140 && !item.widgetConfig.dontSortLayouts) { // text widget
        choosableLayouts.sort((a, b) => {
          return String(a).localeCompare(String(b));
        });
      }

      if (choosableLayouts.indexOf(item.currentLayout) < 0) {
        choosableLayouts.unshift(item.currentLayout);
      }
      this.layoutsDataSource = choosableLayouts;
    }
    else if (item && availableLayouts) {
      this.layoutsDataSource = availableLayouts;
    }
    else {
      this.layoutsDataSource = [];
    }
  }

  /**
   * Gets the choosable calculations
   * @param item
   * @param availableCalculations
   * @param usedCalculations
   * @returns
   */
  private getChoosableCalculations(item: WidgetBaseComponent, availableCalculations: ReportDesignerWidgetCalculationOption[], usedCalculations: number[]): ReportDesignerWidgetCalculationOption[] {
    const choosableCalculations: ReportDesignerWidgetCalculationOption[] = [];
    for (const calc of availableCalculations) {
      if (usedCalculations.indexOf(calc.id) < 0) {
        choosableCalculations.push(calc);
      }
    }

    if (!item.widgetConfig.dontSortCalculations) {
      choosableCalculations.sort((a, b) => {
        return a.description.localeCompare(b.description);
      });
    }

    return choosableCalculations;
  }

  /**
   * Gets the already used calculations
   * @param activeDocument
   * @param item
   * @returns
   */
  private getUsedCalculations(activeDocument: ReportDesignerDocumentComponent, item: WidgetBaseComponent): number[] {
    const usedCalculations: number[] = [];
    for (const page of activeDocument.data.pages) {
      for (const widget of page.widgets) {
        if (widget.widgetId == item.widgetConfig.widgetId) {
          if (usedCalculations.indexOf(widget.widgetSettings.currentCalculation) < 0) {
            usedCalculations.push(Number(widget.widgetSettings.currentCalculation));
          }
        }
      }
    }
    return usedCalculations;
  }

  /**
   * Gets the choosable layouts
   * @param availableLayouts
   * @param usedLayouts
   * @returns
   */
  private getChoosableLayouts(availableLayouts: string[], usedLayouts: string[]): string[] {
    const choosableLayouts: string[] = [];
    for (const layout of availableLayouts) {
      if (usedLayouts.indexOf(layout) < 0) {
        choosableLayouts.push(layout);
      }
    }
    return choosableLayouts;
  }

  /**
   * Gets the used layouts
   * @param activeDocument
   * @param item
   * @returns
   */
  private getUsedLayouts(activeDocument: ReportDesignerDocumentComponent, item: WidgetBaseComponent): string[] {
    const usedLayouts: string[] = [];
    for (const page of activeDocument.data.pages) {
      for (const widget of page.widgets) {
        if (widget.widgetId == item.widgetConfig.widgetId) {
          if (usedLayouts.indexOf(widget.widgetSettings.currentLayout) < 0) {
            usedLayouts.push(widget.widgetSettings.currentLayout);
          }
        }
      }
    }
    return usedLayouts
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.onEvents.push(this.eventBus.subscribeEvent('toggleEmptyHashtagsNavigator', () => {
      this.showEmptyHashtags = !this.showEmptyHashtags;
      this.eventBus.broadcastEvent('checkEmptyHashtags');
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('widgetRemoved', (widgetId) => {
      if (this.activeWidget?.widgetConfig && widgetId == this.activeWidget.widgetConfig.widgetSettings.id) {
        this.layoutsDataSource = [];
        this.calculationDataSource = [];

        this.activeWidget = null;
      }
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('activateInfobar', (item: WidgetBaseComponent) => {
      this.activateInfobar(item);
      this.selectedTabIndex = 0;
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('activateInfobarForHashtag', (item) => {
      this.activateInfobar(item);
      this.selectedTabIndex = 1;
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('displayHashTagsTab', (item) => {
      this.activateInfobar(item);
      this.selectedTabIndex = 1;
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('widgetsChanged', () => {
      this.activateInfobar(this.activeWidget);
    }));
  }
}
